import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { identify } from '@/lib/visitor-tracking';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

export const WaitlistForm = () => {
  const { t } = useTranslation();
  
  // Input validation schema
  const waitlistSchema = z.object({
    name: z.string().trim().min(1, t('waitlistForm.validation.nameRequired')).max(100, t('waitlistForm.validation.nameMax')),
    email: z.string().trim().email(t('waitlistForm.validation.invalidEmail')).max(255, t('waitlistForm.validation.emailMax')),
    company: z.string().trim().max(100, t('waitlistForm.validation.companyMax')).optional().or(z.literal('')),
    phone: z.string().trim().max(20, t('waitlistForm.validation.phoneMax')).optional().or(z.literal('')),
    message: z.string().trim().max(1000, t('waitlistForm.validation.messageMax')).optional().or(z.literal(''))
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Check if user came from exit popup
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');
    const fromExitPopup = source === 'exit-popup-free-trial' || source === 'exit-popup';
    
    // Check for hidden input field added by exit popup
    const hiddenSourceInput = document.querySelector<HTMLInputElement>('input[name="source"][type="hidden"]');
    const hasExitPopupSource = hiddenSourceInput?.value === 'exit-popup-free-trial';
    
    if ((fromExitPopup || hasExitPopupSource) && !formData.message) {
      setFormData(prev => ({
        ...prev,
        message: '🎁 [FREE TRIAL REQUEST FROM EXIT POPUP] Interested in 1-month free trial - No credit card needed'
      }));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate input before submission
      const validation = waitlistSchema.safeParse(formData);
      
      if (!validation.success) {
        const firstError = validation.error.errors[0];
        toast({
          title: t('waitlistForm.toast.validationError'),
          description: firstError.message,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Check for exit popup source marker
      const hiddenSourceInput = document.querySelector<HTMLInputElement>('input[name="source"][type="hidden"]');
      const urlParams = new URLSearchParams(window.location.search);
      const isExitPopupSource = 
        hiddenSourceInput?.value === 'exit-popup-free-trial' || 
        urlParams.get('source') === 'exit-popup-free-trial';

      // Prepare validated data for submission
      const submissionData = {
        name: validation.data.name,
        email: validation.data.email,
        company: validation.data.company || null,
        phone: validation.data.phone || null,
        message: isExitPopupSource 
          ? `🎁 [FREE TRIAL REQUEST] ${validation.data.message || 'Interested in 1-month free trial via exit popup'}` 
          : validation.data.message || null
      };

      const { error } = await supabase
        .from('waitlist')
        .insert([submissionData]);

      if (error) {
        // Don't expose internal database error details
        console.error('Waitlist submission error:', error);
        
        // Map specific error codes to user-friendly messages
        if (error.code === '23505') {
          toast({
            title: t('waitlistForm.toast.alreadyReceived'),
            description: t('waitlistForm.toast.alreadyReceivedDesc'),
            variant: "default"
          });
        } else {
          // Generic error message - don't expose database details
          toast({
            title: t('waitlistForm.toast.unableToSubmit'),
            description: t('waitlistForm.toast.checkInfo'),
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: t('waitlistForm.toast.requestReceived'),
          description: t('waitlistForm.toast.requestReceivedDesc'),
        });
        identify(validation.data.email, { name: validation.data.name, company: validation.data.company || undefined, via: 'form' }).catch(() => {});
        
        
        // Notify founder
        supabase.functions.invoke('notify-founder', {
          body: {
            type: isExitPopupSource ? 'exit_popup_signup' : 'waitlist_signup',
            name: validation.data.name,
            email: validation.data.email,
            company: validation.data.company || '',
            message: validation.data.message || '',
          },
        }).catch(console.error);
        
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      // Generic error message for unexpected errors
      console.error('Unexpected error:', error);
      toast({
        title: t('waitlistForm.toast.unableToSubmit'),
        description: t('waitlistForm.toast.tryLater'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-md mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          name="name"
          type="text"
          placeholder={t('waitlistForm.placeholders.name')}
          value={formData.name}
          onChange={handleInputChange}
          required
          maxLength={100}
          className="h-12 bg-background/10 border-background/20 text-background placeholder:text-background/60 focus:border-background/40 rounded-xl"
        />
        <Input
          name="email"
          type="email"
          placeholder={t('waitlistForm.placeholders.email')}
          value={formData.email}
          onChange={handleInputChange}
          required
          maxLength={255}
          className="h-12 bg-background/10 border-background/20 text-background placeholder:text-background/60 focus:border-background/40 rounded-xl"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          name="company"
          type="text"
          placeholder={t('waitlistForm.placeholders.company')}
          value={formData.company}
          onChange={handleInputChange}
          maxLength={100}
          className="h-12 bg-background/10 border-background/20 text-background placeholder:text-background/60 focus:border-background/40 rounded-xl"
        />
        <Input
          name="phone"
          type="tel"
          placeholder={t('waitlistForm.placeholders.phone')}
          value={formData.phone}
          onChange={handleInputChange}
          maxLength={20}
          className="h-12 bg-background/10 border-background/20 text-background placeholder:text-background/60 focus:border-background/40 rounded-xl"
        />
      </div>
      <Textarea
        name="message"
        placeholder={t('waitlistForm.placeholders.message')}
        value={formData.message}
        onChange={handleInputChange}
        maxLength={1000}
        className="bg-background/10 border-background/20 text-background placeholder:text-background/60 focus:border-background/40 min-h-[80px] rounded-xl"
      />
      <Button 
        type="submit"
        size="lg"
        className="h-12 bg-background text-foreground hover:bg-background/90 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
        disabled={isSubmitting}
      >
        {isSubmitting ? t('waitlistForm.submitting') : t('waitlistForm.submit')}
      </Button>
    </form>
  );
};
